import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, DollarSign, Tag } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { LoadingSpinner } from './LoadingSpinner';
import { Post } from '../types';
import { PROVIDER_MESSAGE_TEMPLATES, ROUTES } from '../constants';
import { startProviderChat } from '../services/api';

interface ProviderTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  providerId: number;
}

export const ProviderTemplateModal: React.FC<ProviderTemplateModalProps> = ({
  isOpen,
  onClose,
  post,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTemplateSelect = async (templateText: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await startProviderChat({
        userId: post.userId,
        postId: post.postId,
        text: templateText,
      });

      if (response.success) {
        onClose();
        navigate(ROUTES.MESSAGES);
      } else {
        setError(response.error || 'Failed to start chat. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Contact Care Request">
      <div className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-gray-900">{post.title}</h3>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <Tag className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-500">Care Type</p>
                <p className="text-gray-900 font-medium">{post.careType}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-500">Location</p>
                <p className="text-gray-900 font-medium">{post.city}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-500">Appointment</p>
                <p className="text-gray-900 font-medium">
                  {new Date(post.appointmentDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <DollarSign className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-500">Budget</p>
                <p className="text-gray-900 font-medium">£{post.budget}</p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Select a message to send:</h4>

          {PROVIDER_MESSAGE_TEMPLATES.map((template, index) => (
            <button
              key={index}
              onClick={() => handleTemplateSelect(template)}
              disabled={loading}
              className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <p className="text-sm text-gray-700 group-hover:text-gray-900 leading-relaxed">
                {template}
              </p>
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <LoadingSpinner size="md" />
            <span className="ml-3 text-gray-600">Starting chat...</span>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};
